import { useRef } from "react"
import { Employee } from "types/Employee"
import {
  CarrotPlanFormOption,
  MenuItemOptionFields,
  TopicOptionFields,
  useCarrotPlanMappings
} from "./useCarrotPlanMappings"
import { UnslottedStep } from "../CarrotPlanFormTypes"
import { NONE_VALUE } from "../JourneySelection"

type UseDynamicCarrotPlanMappingsArgs = {
  employee: Employee
  initialMenuItems: CarrotPlanFormOption<MenuItemOptionFields>[]
  initialTopics: CarrotPlanFormOption<TopicOptionFields>[]
  journey: number
  stage: number
  enableRecommendations?: boolean
}

type UseDynamicCarrotPlanMappingsResult = {
  menuItems: CarrotPlanFormOption<MenuItemOptionFields>[]
  topics: CarrotPlanFormOption<TopicOptionFields>[]
  recommendedSteps: UnslottedStep[] | null
  recommendationsErrorStatus?: number
  isLoading: boolean
}

/**
 * Resolves the menu items + topics to display for the current (journey, stage):
 * the parent-supplied initials when the user hasn't moved off the initial selection,
 * otherwise a fresh fetch for the new selection. `isLoading` only reflects the options
 * fetch path. Recommendations are fetched whenever `enableRecommendations` is true and
 * both journey + stage are set, keyed on the (journey, stage) tuple.
 */
export function useDynamicCarrotPlanMappings({
  employee,
  initialMenuItems,
  initialTopics,
  journey,
  stage,
  enableRecommendations = false
}: UseDynamicCarrotPlanMappingsArgs): UseDynamicCarrotPlanMappingsResult {
  const initialSelectionRef = useRef({ journey, stage })

  const hasJourneySelectionChanged =
    journey !== initialSelectionRef.current.journey || stage !== initialSelectionRef.current.stage
  const bothJourneyAndStageSelected = journey !== NONE_VALUE && stage !== NONE_VALUE
  const shouldFetchNewOptions = hasJourneySelectionChanged && bothJourneyAndStageSelected
  const shouldFetchRecommendations = enableRecommendations && bothJourneyAndStageSelected

  // Pass journey/stage when either path needs them. On the initial selection the
  // query key matches the parent's prefetch, so react-query dedupes — no extra fetch.
  const effectiveJourney = shouldFetchNewOptions || shouldFetchRecommendations ? journey : undefined
  const effectiveStage = shouldFetchNewOptions || shouldFetchRecommendations ? stage : undefined

  const {
    mappedFormMenuItems: fetchedMenuItems,
    mappedFormTopics: fetchedTopics,
    recommendedSteps,
    recommendationsErrorStatus,
    areMappingsLoading
  } = useCarrotPlanMappings(employee, effectiveJourney, effectiveStage, shouldFetchRecommendations)

  return {
    menuItems: shouldFetchNewOptions ? fetchedMenuItems : initialMenuItems,
    topics: shouldFetchNewOptions ? fetchedTopics : initialTopics,
    recommendedSteps,
    recommendationsErrorStatus,
    isLoading: shouldFetchNewOptions && areMappingsLoading
  }
}
